import { Component, Host, h, Element, Prop, Fragment, Watch, State, Event, EventEmitter } from "@stencil/core";
import pfx from "../../../utils/style-class-prefix-helper";
import {
  BADGE_IMAGE_ALIGNMENT_CENTER,
  BADGE_TITLE_ALIGNMENT_CENTER,
  BADGE_SCHEME_CONFIG,
  BADGE_SCHEME_MAP
} from "./constants/badge-constants";
import {
  BadgeImageAlignment,
  BadgeSchemeDefinition,
  BadgeSchemeName,
  BadgeTitleAlignment,
  ImageSaturation
} from "./constants/badge-types";
import { LinkLocalization, LinkTarget } from "../../link/constants/link-types";
import { TARGET_SELF } from "../../link/constants/link-constants";
import { computeLinkAriaLabel } from "../../../utils/link-helper";
import { Localization, setCurrentLocalizationDelegate } from "../../../common/localizable-component";
import { LINK_LOCALIZATION } from "../../link/constants/link-i18n";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../../common/colorable-component";
import { BadgeSchemeConfiguration } from "../../../constants/colors";
// import { InitLogging } from "../../../decorators/init-logging";

@Component({
  tag: "eon-ui-badge",
  styleUrl: "badge.scss",
  shadow: true
})
export class Badge implements ColorableComponent<BadgeSchemeConfiguration<BadgeSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  localization: Localization<LinkLocalization> = LINK_LOCALIZATION;
  currentLocalization: LinkLocalization;

  schemeConfig: BadgeSchemeConfiguration<BadgeSchemeDefinition> = BADGE_SCHEME_CONFIG;
  schemeMap = BADGE_SCHEME_MAP;

  @Element() hostElement: HTMLEonUiBadgeElement;
  private currentAriaLabel: string;
  private rel: string = "";
  private title: string = "";

  @State() activeScheme: BadgeSchemeDefinition;

  /**
   * Source to badge image. Can be any browser supported image file.
   */
  @Prop() imageSrc: string;

  /**
   * Title of the badge to describe it in more detail.
   */
  @Prop() description: string;

  /**
   * Source to badge link.
   */
  @Prop({ mutable: true }) href: string;

  /**
   * Specifies how the link is opened in the browser, e.g. in new window/tab.
   * @example "_blank", "_self", "_parent", "_top"
   * @default "_self"
   */
  @Prop({ mutable: true }) target: LinkTarget = TARGET_SELF;

  /**
   * Whether this button can be focused by a keyboard. Defaults to true.
   */
  @Prop({ mutable: true }) navigable: boolean = true;

  /**
   * Specifies the label to use for accessibility.
   */
  @Prop({ mutable: true }) ariaLabel: string;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: BadgeSchemeName;

  /**
   * Defines the width of the image.
   */
  @Prop({ mutable: true }) imageScale: string;

  /**
   *
   * Vertical alignment of image.
   * @default center
   */
  @Prop({ mutable: true, reflect: true }) imageAlignment: BadgeImageAlignment = BADGE_IMAGE_ALIGNMENT_CENTER;

  /**
   *
   * Vertical alignment of label.
   * @default center
   */
  @Prop({ mutable: true, reflect: true }) titleAlignment: BadgeTitleAlignment = BADGE_TITLE_ALIGNMENT_CENTER;

  /**
   * This property saturates or desaturates the image.
   */
  @Prop({ mutable: true, reflect: true }) imageSaturation: ImageSaturation;

  /**
   * Raised when the badge has been clicked.
   */
  @Event() badgeClick: EventEmitter;

  /**
   * Raised when the badge has been loaded.
   */
  @Event() badgeLoad: EventEmitter;

  componentWillLoad() {
    this.setCurrentLocalization();
    this.applyScheme();
    this.setTitleAttribute();
    this.computeAriaLabel();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.setImageCSSVars();
    this.badgeLoad.emit();
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  setTitleAttribute(): string {
    this.title = this.hostElement.getAttribute("title") || this.description;
    return this.title ? this.title : "";
  }

  @Watch("href")
  private computeAriaLabel() {
    if (this.ariaLabel || !this.currentLocalization) {
      this.currentAriaLabel = this.ariaLabel;
      return;
    }
    this.currentAriaLabel = computeLinkAriaLabel(this.currentLocalization, this.href, "");
  }

  @Watch("target")
  setRelAttribute(): void {
    this.rel = this.target === "_blank" ? "noopener noreferrer" : "";
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("imageSrc")
  @Watch("imageScale")
  @Watch("imageAlignment")
  setImageCSSVars() {
    this.imageSrc
      ? this.hostElement.style.setProperty("--imageSrc", `url(${this.imageSrc})`)
      : this.hostElement.style.removeProperty("--imageSrc");
    this.imageScale
      ? this.hostElement.style.setProperty("--imageScale", `auto ${this.imageScale}`)
      : this.hostElement.style.removeProperty("--imageScale");
    this.imageAlignment
      ? this.hostElement.style.setProperty("--imageVerticalAlignment", `${this.imageAlignment} center`)
      : this.hostElement.style.removeProperty("--imageVerticalAlignment");
  }

  renderBadgeContent = () => {
    return (
      <Fragment>
        <div
          class={pfx({
            "badge-image": true,
            [`saturation-${this.imageSaturation}`]: !!this.imageSaturation
          })}
        >
          <div class={pfx({ image: true })}></div>
        </div>
        {this.description && (
          <div class={pfx({ "badge-title-wrapper": true, [`alignment-${this.titleAlignment}`]: true })}>
            <span class={pfx({ "badge-title": true })}>{this.description}</span>
          </div>
        )}
      </Fragment>
    );
  };

  render() {
    const ariaLabel = this.ariaLabel || this.currentAriaLabel;

    return (
      <Host>
        <Fragment>
          {!!this.href ? (
            <a
              title={this.title}
              class={pfx({ "badge-wrapper": true, "badge-link": true })}
              {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
              tabIndex={this.navigable ? 0 : -1}
              href={this.href}
              target={this.target}
              rel={this.rel}
              onClick={() => this.badgeClick.emit(this.href)}
            >
              {this.renderBadgeContent()}
            </a>
          ) : (
            <div class={pfx({ "badge-wrapper": true })}>{this.renderBadgeContent()}</div>
          )}
        </Fragment>
        <slot />
      </Host>
    );
  }
}
